import { ATTRIBUTE_PATH_SEPARATOR, buildCustomAttributeJsonPath, CUSTOM_ATTRIBUTE_READ_CAST, parseCustomAttributeJsonPath, } from '@provision/attribute-utils';
import { isInCustomSchedule, isWithinBusinessHours, } from './business-hours-utils';
import { getLocalDateParts, isWeekDay, toTimeParts } from './date-timezone-utils';
/** The behaviors engine's Hours condition (`BusinessHoursType.Partner`), read on the partner clock. */
export const PARTNER_BUSINESS_HOURS_ATTRIBUTE = 'partnerBusinessHours';
/** Week-day + time-range windows — the escalation rules' `customSchedule` shape. */
export const CUSTOM_SCHEDULE_ATTRIBUTE = 'customSchedule';
export const PARTNER_BUSINESS_HOURS_OPTIONS = [
    { value: 'in', label: 'Open' },
    { value: 'not_in', label: 'Closed' },
];
export const POLICY_TIME_CONDITION_ATTRIBUTES = [
    { attribute: PARTNER_BUSINESS_HOURS_ATTRIBUTE, label: 'Partner Business' },
    { attribute: CUSTOM_SCHEDULE_ATTRIBUTE, label: 'Custom schedule' },
];
const BASE_POLICY_CONDITION_ATTRIBUTES = [
    {
        attribute: 'addressId',
        label: 'Address',
        getValues: (context) => context.addressIds,
    },
    {
        attribute: 'clientId',
        label: 'Client',
        getValues: (context) => context.clientIds,
    },
    {
        attribute: 'clientToAddressId',
        label: 'Client/Site link ID',
        getValues: (context) => context.clientToAddressIds,
    },
    {
        attribute: 'partnerId',
        label: 'Partner ID',
        getValues: (context) => context.partnerIds.size ? context.partnerIds : new Set([String(context.partnerId)]),
    },
    {
        attribute: 'partnerGroupId',
        label: 'Partner Group',
        getValues: (context) => context.partnerGroupIds,
    },
    {
        attribute: 'urgency',
        label: 'Conversation urgency',
        getValues: (context) => context.urgencies,
    },
    {
        attribute: 'siteLabelId',
        label: 'Site label',
        getValues: (context) => context.siteLabelIds,
    },
    {
        attribute: 'serviceLevel',
        label: 'Service level',
        getValues: (context) => context.serviceLevels,
    },
    {
        attribute: 'clientSegmentId',
        label: 'Client segment',
        getValues: (context) => context.clientSegmentIds,
    },
    {
        attribute: 'conversationType',
        label: 'Conversation type',
        getValues: (context) => context.conversationTypes,
    },
];
const LEGACY_POLICY_CONDITION_ATTRIBUTES = [
    {
        attribute: 'brandName',
        label: 'Brand (legacy)',
        getValues: (context) => context.clientSegmentIds,
    },
];
export const POLICY_CONDITION_ATTRIBUTES = BASE_POLICY_CONDITION_ATTRIBUTES;
const POLICY_CONDITION_ATTRIBUTE_MAP = new Map([...BASE_POLICY_CONDITION_ATTRIBUTES, ...LEGACY_POLICY_CONDITION_ATTRIBUTES].map((metadata) => [metadata.attribute, metadata]));
const POLICY_CUSTOM_PROPERTY_ENTITIES = [
    'Conversation',
    'Address',
];
/**
 * Custom-property types a policy condition can target. Set types match with In / Not in; the two
 * date types match with Before / After / On / Is empty / Is not empty instead. Lookups stay out —
 * they are not comparable anywhere in the product.
 */
const POLICY_CUSTOM_PROPERTY_TYPES = [
    'short_text',
    'multi_line_text',
    'url',
    'number',
    'checkbox',
    'dropdown',
    'multi_select',
    'date',
    'date_time',
];
const POLICY_CUSTOM_PROPERTY_CASTS = new Set(POLICY_CUSTOM_PROPERTY_TYPES.map((type) => CUSTOM_ATTRIBUTE_READ_CAST[type]));
/** Both date types read as `::timestamptz`, so the cast alone routes a condition to date matching. */
const POLICY_DATE_CAST = CUSTOM_ATTRIBUTE_READ_CAST.date;
/** A date_time compares at the precision its picker offers. */
const MINUTE_MS = 60000;
/**
 * Relative values a date condition can carry instead of a fixed ISO date, resolved against the
 * context's `currentTime` at evaluation. The labels are the wire values and must stay identical to
 * the portal's `RelativeDate` enum (the condition builder writes them) and to the behaviors
 * rolling-date config, which offers the same vocabulary for the same reason.
 */
export const POLICY_RELATIVE_DATES = {
    Today: (now) => now,
    Tomorrow: (now) => addDays(now, 1),
    Yesterday: (now) => addDays(now, -1),
    'One Week Ago': (now) => addDays(now, -7),
    'One Week From Now': (now) => addDays(now, 7),
    '30 Days Ago': (now) => addDays(now, -30),
    '60 Days Ago': (now) => addDays(now, -60),
    '90 Days Ago': (now) => addDays(now, -90),
};
function addDays(date, days) {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}
export function isPolicyCustomPropertyType(type) {
    return POLICY_CUSTOM_PROPERTY_TYPES.includes(type);
}
/** Whether a supported type matches with the date operators rather than In / Not in. */
export function isPolicyDatePropertyType(type) {
    return CUSTOM_ATTRIBUTE_READ_CAST[type] === POLICY_DATE_CAST;
}
/** The read cast the condition attribute carries for a supported type. */
export function getPolicyCustomPropertyCast(type) {
    return CUSTOM_ATTRIBUTE_READ_CAST[type];
}
export function buildCustomPropertyConditionAttribute(entity, key, cast) {
    return `${entity}${ATTRIBUTE_PATH_SEPARATOR}${buildCustomAttributeJsonPath(key, cast)}`;
}
export function parseCustomPropertyConditionAttribute(attribute) {
    if (typeof attribute !== 'string') {
        return null;
    }
    const separatorIndex = attribute.indexOf(ATTRIBUTE_PATH_SEPARATOR);
    if (separatorIndex === -1) {
        return null;
    }
    const entity = attribute.slice(0, separatorIndex);
    if (!isPolicyCustomPropertyEntity(entity)) {
        return null;
    }
    const path = parseCustomAttributeJsonPath(attribute.slice(separatorIndex + 1));
    if (!path?.key) {
        return null;
    }
    return { entity, key: path.key, cast: path.cast };
}
/**
 * The stored `custom_attributes` column as a key → value map. Accepts the JSON text the entity
 * layer returns or an already-parsed object; null, malformed JSON and non-objects read as empty.
 */
export function parseCustomAttributesBlob(raw) {
    if (raw === null || raw === undefined) {
        return {};
    }
    if (typeof raw === 'string') {
        try {
            return parseCustomAttributesBlob(JSON.parse(raw));
        }
        catch {
            return {};
        }
    }
    return typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
}
/**
 * The stored value of a Custom schedule condition: one window per selected day. Entries that are
 * not `{ weekDay: 0–6, timeFrom: 'HH:mm', timeTo: 'HH:mm' }` are dropped; anything but an array
 * reads as no windows.
 */
export function parseScheduleWindows(value) {
    return Array.isArray(value) ? value.filter(isTimeWindow) : [];
}
function isTimeWindow(value) {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const { weekDay, timeFrom, timeTo } = value;
    return isWeekDay(weekDay) && isClockTime(timeFrom) && isClockTime(timeTo);
}
function isClockTime(value) {
    if (typeof value !== 'string') {
        return false;
    }
    const parts = toTimeParts(value);
    return (parts !== null &&
        parts.hour >= 0 &&
        parts.hour <= 23 &&
        parts.minute >= 0 &&
        parts.minute <= 59);
}
export function isPolicyTimeConditionAttribute(attribute) {
    return POLICY_TIME_CONDITION_ATTRIBUTES.some((metadata) => metadata.attribute === attribute);
}
function isPolicyCustomPropertyEntity(value) {
    return POLICY_CUSTOM_PROPERTY_ENTITIES.includes(value);
}
function isPolicyConditionAttribute(attribute) {
    return (typeof attribute === 'string' &&
        POLICY_CONDITION_ATTRIBUTE_MAP.has(attribute));
}
export function getApplicablePolicies(policies, context) {
    return policies.filter((policy) => isPolicyApplicable(policy, context));
}
export function isPolicyApplicable(policy, context) {
    if (policy.archivedAt) {
        return false;
    }
    if (policy.validUntil && new Date(policy.validUntil) < new Date()) {
        return false;
    }
    const policyGroupIds = extractPolicyGroupIds(policy.userGroups);
    if (context.userGroupIds && policyGroupIds.length) {
        const hasAccess = policyGroupIds.some((id) => id && context.userGroupIds?.includes(id));
        if (!hasAccess) {
            return false;
        }
    }
    const addressIds = normalizeIdArray(policy.addressIds);
    const clientIds = normalizeIdArray(policy.clientIds);
    const clientToAddressIds = normalizeIdArray(policy.clientToAddressIds);
    const hasScope = addressIds.length || clientIds.length || clientToAddressIds.length;
    const matchesScope = !hasScope ||
        addressIds.some((id) => context.addressIds.has(id)) ||
        clientIds.some((id) => context.clientIds.has(id)) ||
        clientToAddressIds.some((id) => context.clientToAddressIds.has(id));
    if (!matchesScope) {
        return false;
    }
    return evaluateConditions(policy.conditions, context);
}
/** Whether a policy's stored conditions mention any custom property — lets callers skip loading `custom_attributes` blobs when no policy needs them. */
export function conditionsReferenceCustomProperty(rawConditions) {
    return conditionsSome(rawConditions, (condition) => parseCustomPropertyConditionAttribute(condition.attribute) !== null);
}
/** Whether a policy's stored conditions read the partner clock — lets callers skip loading hours, holidays and timezone otherwise. */
export function conditionsReferenceTimeCondition(rawConditions) {
    return conditionsSome(rawConditions, (condition) => isPolicyTimeConditionAttribute(condition.attribute));
}
function conditionsSome(rawConditions, predicate) {
    let conditionGroup = null;
    try {
        conditionGroup = parseConditionGroup(rawConditions);
    }
    catch {
        return false;
    }
    return conditionGroup ? conditionElementSome(conditionGroup, predicate) : false;
}
function conditionElementSome(condition, predicate) {
    if (condition.elementType === 'conditionGroup') {
        return (Array.isArray(condition.conditionsElements) ? condition.conditionsElements : []).some((element) => conditionElementSome(element, predicate));
    }
    return isDataConditionNode(condition) && predicate(condition);
}
function extractPolicyGroupIds(userGroups) {
    if (!Array.isArray(userGroups)) {
        return [];
    }
    return userGroups
        .map((group) => {
        if (group && typeof group === 'object') {
            const typedGroup = group;
            return normalizeValue(typedGroup.id) ?? normalizeValue(typedGroup.userGroupId);
        }
        return null;
    })
        .filter((value) => Boolean(value));
}
function evaluateConditions(rawConditions, context) {
    if (!rawConditions)
        return true;
    let conditionGroup = null;
    try {
        conditionGroup = parseConditionGroup(rawConditions);
    }
    catch (error) {
        console.error('Failed to parse support policy conditions', { error });
        return true;
    }
    if (!conditionGroup)
        return true;
    return evaluateConditionElement(conditionGroup, context);
}
function parseConditionGroup(rawConditions) {
    if (!rawConditions)
        return null;
    if (typeof rawConditions === 'object' &&
        rawConditions !== null &&
        'elementType' in rawConditions) {
        return rawConditions;
    }
    if (typeof rawConditions === 'string') {
        return JSON.parse(rawConditions);
    }
    return null;
}
function evaluateConditionElement(condition, context) {
    if (condition.elementType === 'conditionGroup') {
        return evaluateConditionGroup(condition, context);
    }
    return isDataConditionNode(condition) ? evaluateConditionNode(condition, context) : true;
}
function evaluateConditionGroup(group, context) {
    const elements = Array.isArray(group.conditionsElements) ? group.conditionsElements : [];
    const results = elements
        .filter((element) => !isCustomScheduleCondition(element))
        .map((element) => evaluateConditionElement(element, context));
    // a second time range is a second Custom schedule condition, so sibling schedules OR together
    // even under `and` — two windows can never both contain the same moment
    const schedules = elements.filter(isCustomScheduleCondition);
    if (schedules.length) {
        results.push(schedules.some((schedule) => evaluateConditionElement(schedule, context)));
    }
    return group.operator === 'or' ? results.some(Boolean) : results.every(Boolean);
}
function isCustomScheduleCondition(condition) {
    return isDataConditionNode(condition) && condition.attribute === CUSTOM_SCHEDULE_ATTRIBUTE;
}
function isDataConditionNode(condition) {
    return (condition.elementType === 'conditionNode' &&
        condition.conditionType === 'data' &&
        typeof condition.attribute === 'string');
}
function evaluateConditionNode(condition, context) {
    const customProperty = parseCustomPropertyConditionAttribute(condition.attribute);
    if (customProperty) {
        return evaluateCustomPropertyCondition(condition, customProperty, context);
    }
    if (isPolicyTimeConditionAttribute(condition.attribute)) {
        return evaluateTimeCondition(condition.attribute, condition, context);
    }
    const values = normalizeConditionValues(condition.value);
    if (!values.length)
        return false;
    if (!isPolicyConditionAttribute(condition.attribute)) {
        return false;
    }
    const targetSet = getTargetSet(condition.attribute, context);
    if (!targetSet)
        return false;
    return matchesOperator(condition.operator, values, targetSet);
}
function evaluateCustomPropertyCondition(condition, target, context) {
    // an unknown cast means a type this build cannot compare — the policy stays hidden rather than
    // applying to everyone
    if (!POLICY_CUSTOM_PROPERTY_CASTS.has(target.cast)) {
        return false;
    }
    if (target.cast === POLICY_DATE_CAST) {
        return evaluateDatePropertyCondition(condition, target, context);
    }
    const values = normalizeConditionValues(condition.value)
        .map((value) => normalizeCustomPropertyValue(value, target.cast))
        .filter((value) => value !== null);
    if (!values.length)
        return false;
    return matchesOperator(condition.operator, values, getCustomPropertyTargetSet(target, context));
}
/**
 * Date properties compare as instants against a single operand, except On, which compares calendar
 * days in the partner's timezone — an exact-timestamp equality would never match what a user picked.
 * An entity with no usable date matches no comparison, in either direction; only Is empty answers
 * for it. Is empty and Is not empty take no operand.
 */
function evaluateDatePropertyCondition(condition, target, context) {
    if (condition.operator === 'empty' || condition.operator === 'not_empty') {
        const wantsDate = condition.operator === 'not_empty';
        // one value per entity in scope, so with none in scope neither operator matches
        return readCustomPropertyValues(target, context).some((value) => (parseStoredDate(value) !== null) === wantsDate);
    }
    const [rawOperand] = normalizeConditionValues(condition.value);
    const operand = resolveConditionDate(rawOperand, context.currentTime ?? new Date());
    if (!operand)
        return false;
    // both sides collapse to the same granularity first, so a value is exactly one of before / On /
    // after: a date has no time of day and compares as a calendar day on the partner clock, while a
    // date_time compares as the instant the picker offers, to the minute
    const toComparable = isDateTimeProperty(target, context)
        ? (date) => Math.floor(date.getTime() / MINUTE_MS)
        : (date) => toLocalDayOrdinal(date, context.timezone);
    const operandValue = toComparable(operand);
    return collectStoredDates(target, context).some((stored) => {
        switch (condition.operator) {
            case 'before':
                return toComparable(stored) < operandValue;
            case 'after':
                return toComparable(stored) > operandValue;
            case '=':
                return toComparable(stored) === operandValue;
            default:
                return false;
        }
    });
}
function resolveConditionDate(value, now) {
    if (!value)
        return null;
    const relative = POLICY_RELATIVE_DATES[value];
    if (relative) {
        return relative(now);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function collectStoredDates(target, context) {
    return readCustomPropertyValues(target, context).flatMap((value) => {
        const parsed = parseStoredDate(value);
        return parsed ? [parsed] : [];
    });
}
function parseStoredDate(value) {
    const parsed = typeof value === 'string' ? new Date(value) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}
function isDateTimeProperty(target, context) {
    return getCustomPropertyDefinition(target, context)?.type === 'date_time';
}
function getCustomPropertyDefinition(target, context) {
    return context.customPropertyDefinitions?.[target.entity]?.[target.key];
}
/** The calendar day on the partner clock as a sortable `YYYYMMDD` number. */
function toLocalDayOrdinal(date, timezone) {
    const { year, month, day } = getLocalDateParts(date, timezone);
    return year * 10000 + month * 100 + day;
}
function evaluateTimeCondition(attribute, condition, context) {
    // no clock in the context (an older caller) — the policy stays hidden rather than applying at
    // any time of day
    if (!context.currentTime) {
        return false;
    }
    // the partner's own hours and holidays on the partner clock; the escalation cutoff hours the
    // context also carries are for escalation rules only
    const clock = {
        currentTime: context.currentTime,
        timezone: context.timezone,
        businessHours: context.businessHours,
        holidays: context.holidays,
    };
    if (attribute === CUSTOM_SCHEDULE_ATTRIBUTE) {
        return isInCustomSchedule(parseScheduleWindows(condition.value), clock);
    }
    const state = isWithinBusinessHours(clock)
        ? 'in'
        : 'not_in';
    const values = normalizeConditionValues(condition.value).filter(isPartnerBusinessHoursConditionValue);
    return values.length > 0 && matchesOperator(condition.operator, values, new Set([state]));
}
function isPartnerBusinessHoursConditionValue(value) {
    return PARTNER_BUSINESS_HOURS_OPTIONS.some((option) => option.value === value);
}
function matchesOperator(operator, values, targetSet) {
    switch (operator) {
        case '=':
        case 'in':
        case 'is_one_of':
            return values.some((value) => targetSet.has(value));
        case '!=':
        case 'not_in':
        case 'is_not_one_of':
            return values.every((value) => !targetSet.has(value));
        default:
            return false;
    }
}
function getCustomPropertyTargetSet(target, context) {
    const targetSet = new Set();
    for (const stored of readCustomPropertyValues(target, context)) {
        const normalized = normalizeCustomPropertyValue(stored, target.cast);
        if (normalized !== null) {
            targetSet.add(normalized);
        }
    }
    return targetSet;
}
/**
 * The property's values across the entities in scope, flattened: the conversation contributes at
 * most one, each linked site one, and a multi_select contributes each selected option. An entity
 * whose blob never stored the key contributes the definition's default instead, which is what the
 * side panel shows for it. No entity in scope means no values at all — not the default.
 */
function readCustomPropertyValues(target, context) {
    const blobs = target.entity === 'Conversation'
        ? context.conversationCustomAttributes
        : context.addressCustomAttributes;
    const fallback = getCustomPropertyDefinition(target, context)?.defaultValue;
    return blobs.flatMap((blob) => {
        const stored = target.key in blob ? blob[target.key] : fallback;
        return Array.isArray(stored) ? stored : [stored];
    });
}
/**
 * Both sides of a comparison pass through here, so a stored `true` and the Yes option `'true'`, or
 * a stored `"10.0"` and a typed `10`, meet in one form. Text compares trimmed, case-insensitively.
 */
function normalizeCustomPropertyValue(value, cast) {
    if (value === null || value === undefined) {
        return null;
    }
    switch (cast) {
        case '::boolean':
            if (value === true || value === 'true')
                return 'true';
            if (value === false || value === 'false')
                return 'false';
            return null;
        case '::numeric': {
            if (typeof value === 'number') {
                return Number.isFinite(value) ? String(value) : null;
            }
            if (typeof value !== 'string' || value.trim() === '') {
                return null;
            }
            const parsed = Number(value);
            return Number.isFinite(parsed) ? String(parsed) : null;
        }
        case '::text':
        case '::text[]': {
            if (typeof value !== 'string' &&
                typeof value !== 'number' &&
                typeof value !== 'boolean') {
                return null;
            }
            const text = String(value).trim().toLowerCase();
            return text || null;
        }
        default:
            return null;
    }
}
function normalizeConditionValues(value) {
    if (Array.isArray(value)) {
        return value.map((v) => normalizeValue(v)).filter((v) => Boolean(v));
    }
    const normalized = normalizeValue(value);
    return normalized ? [normalized] : [];
}
function normalizeValue(value) {
    if (!value && value !== 0)
        return null;
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number')
        return value.toString();
    if (typeof value === 'boolean')
        return value ? 'true' : 'false';
    if (typeof value === 'object' && value && 'value' in value) {
        const nested = value.value;
        return typeof nested === 'string' || typeof nested === 'number' ? String(nested) : null;
    }
    return null;
}
function getTargetSet(attribute, context) {
    const metadata = POLICY_CONDITION_ATTRIBUTE_MAP.get(attribute);
    return metadata ? metadata.getValues(context) : null;
}
const BRACES_REGEX = /^\{|\}$/g;
const BRACKETS_REGEX = /^\[|\]$/g;
const QUOTES_REGEX = /^"+|"+$/g;
/**
 * Normalizes various forms of array-like or string input into a string array of trimmed, non-empty values.
 *
 * - If the input is an array, returns a trimmed string array without empty values.
 * - If the input is a stringified array (e.g., JSON or comma-separated), parses and returns a trimmed string array.
 * - Removes brackets, braces, and quotes if present, and splits comma-separated values.
 * - Returns an empty array for null, undefined, or empty input.
 *
 * @param value - The input data to normalize (array, string, or other).
 * @returns The normalized array of non-empty, trimmed strings.
 *
 * @example
 * normalizeIdArray(['a', ' b ', '', null]);          // ['a', 'b']
 * normalizeIdArray('["x"," y ",""]');                // ['x', 'y']
 * normalizeIdArray('x, y, z');                       // ['x', 'y', 'z']
 * normalizeIdArray('{a, b, c}');                     // ['a', 'b', 'c']
 * normalizeIdArray('[ "foo" , "bar" ]');             // ['foo', 'bar']
 * normalizeIdArray('');                              // []
 * normalizeIdArray(undefined);                       // []
 */
export function normalizeIdArray(value) {
    if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => String(item).trim()).filter((item) => item.length > 0);
            }
        }
        catch {
            // ignore parse errors and fall back to comma-splitting
        }
        const sanitized = trimmed.replace(BRACES_REGEX, '').replace(BRACKETS_REGEX, '');
        if (!sanitized)
            return [];
        return sanitized
            .split(',')
            .map((item) => item.replace(QUOTES_REGEX, '').trim())
            .filter((item) => item.length > 0);
    }
    return [];
}
